/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/member-ordering */
// Angular Imports
import { Location } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { get as _get, merge as _merge } from 'lodash';
import { Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';

// lodash
// My Account Imports
import { Address, RiskModel } from '@amfam/policy/models';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { AgentInfo, PartyAddress, PolicyChangeRequestType, State } from '@amfam/shared/models';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DsModalService, Option, ValidationService } from '@amfam/ui-kit';

import { AddressChange, ChangeRequest, ChangeRequestPolicies, RequestChange } from '../../models';
import { PolicyTransmuteService } from '../../services/policy-transmute.service';

@Component({
  selector: 'ds-change-request-address-form',
  templateUrl: './change-request-address-form.component.html',
  styleUrls: ['./change-request-address-form.component.scss'],
  standalone: false
})
export class ChangeRequestAddressFormComponent implements OnInit, OnDestroy, OnChanges {
  private stop$ = new Subject<void>();
  private builder = inject(FormBuilder);
  addressChange = this.copyService.getCopy('policy.addressChange');
  states: State[] = this.copyService.getCopy('shared.usStates');
  vehicleId: number = this.location.getState()['vehicleId'];
  agentsModal = 'agentsModal';
  garagedOptions: Option[] = [...this.copyService.getCopy('policy.addressChange.options')];
  documentOptions: Option[] = [...this.copyService.getCopy('policy.addressChange.options')];
  addressForm = this.builder.group(
    {
      address1: new FormControl(
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(68),
          ValidationService.specialCharValidatorWithLetterAndNum,
          ValidationService.addressEntryValidator
        ])
      ),
      address2: new FormControl(
        '',
        Validators.compose([
          ValidationService.specialCharValidatorWithLetterAndNum,
          ValidationService.addressEntryValidator,
          Validators.minLength(2),
          Validators.maxLength(68)
        ])
      ),
      city: new FormControl(
        '',
        Validators.compose([
          Validators.required,
          ValidationService.specialCharValidator,
          Validators.minLength(2),
          Validators.maxLength(30)
        ])
      ),
      state: new FormControl('', Validators.required),
      zip: new FormControl(
        '',
        Validators.compose([Validators.required, ValidationService.zipCodeValidator])
      ),
      sendDocuments: new FormControl<Option>(this.documentOptions[0], Validators.required),
      vehiclesGaraged: new FormControl<Option>(this.garagedOptions[0], Validators.required),
      vehicles: this.builder.array([]),
      reason: new FormControl('')
    },
    { validators: this.validateVehicles }
  );

  vehicleOptions: Option[] = [];
  public get city(): AbstractControl<string> {
    return this.addressForm.get('city');
  }
  public get state(): AbstractControl<string> {
    return this.addressForm.get('state');
  }
  public get zip(): AbstractControl<string> {
    return this.addressForm.get('zip');
  }

  public get address1(): AbstractControl<string> {
    return this.addressForm.get('address1');
  }

  public get address2(): AbstractControl<string> {
    return this.addressForm.get('address2');
  }

  public get vehiclesGaraged(): AbstractControl<Option> {
    return this.addressForm.get('vehiclesGaraged');
  }

  public get sendDocuments(): AbstractControl<Option> {
    return this.addressForm.get('sendDocuments');
  }

  public get vehicles(): FormArray {
    return this.addressForm.get('vehicles') as FormArray;
  }

  public get reason(): AbstractControl {
    return this.addressForm.get('reason');
  }

  get valid(): boolean {
    return this.addressForm.valid;
  }

  @Input() agents: AgentInfo[];
  @Input() addressInfo: Partial<RequestChange>;
  @Input() newTemplate = false;

  private _risks: RiskModel[];
  @Input()
  set risks(value: RiskModel[]) {
    this._risks = value;
  }

  get risks() {
    return this._risks;
  }

  get hasSelectAll() {
    return this.risks.length > 1;
  }

  get selectAll() {
    return this.hasSelectAll ? this.vehicles.controls[0] : undefined;
  }

  @Output() changeRequestAddress = new EventEmitter<ChangeRequestPolicies>();
  @Output() cancelEvent = new EventEmitter();
  @Output() valueChangeEvent = new EventEmitter();

  // OLD TEMPLATE

  // Inputs
  @Input() currentAddress: PartyAddress;

  // FormGroup
  public addressGroup: UntypedFormGroup;

  // Formcontrols
  public streetAddressLine1Input: AbstractControl;
  public cityInput: AbstractControl;
  public streetAddressLine2Input: AbstractControl;
  public stateInput: AbstractControl;
  public zipcodeInput: AbstractControl;
  public commentInputCtrl: AbstractControl;

  // OLD TEMPLATE

  constructor(
    private copyService: CopyService,
    public roleService: ImpersonateRolesService,
    private analyticsFacade: AnalyticsFacade,
    private location: Location,
    private modalService: DsModalService,
    private formBuilder: UntypedFormBuilder, // OLD TEMPLATE
    private policyTransmuteService: PolicyTransmuteService // OLD TEMPLATE
  ) {}

  ngOnInit() {
    if (this.newTemplate) this.analyticsFacade.trackEvent({ event: 'update_address_start' });
    else this.initializeOldTemplate();
  }

  private updateAll(selected: boolean) {
    this.vehicles.controls.forEach((control, i) => {
      if (i > 0 && control.value !== selected) control.setValue(selected, { emitEvent: false });
    });
  }

  private attachEventHandler() {
    this.vehiclesGaraged?.valueChanges
      .pipe(
        takeUntil(this.stop$),
        tap(option => {
          this.addRemoveVehicles(option.value);
        })
      )
      .subscribe();
  }

  private addRemoveVehicles(add: boolean) {
    if (add) {
      this.setFormArray();
    } else this.vehicles.clear();
  }

  private getVehicleControl() {
    return this.builder.control({
      value: false,
      disabled: false
    });
  }

  private setFormArray() {
    if (this.risks?.length > 0) {
      this.setVehicleOptions(this.risks);
      let vehicles = this.risks.map(vehicle =>
        this.builder.control({
          value: Number(vehicle.vehicleId) === Number(this.vehicleId),
          disabled: false
        })
      );

      if (this.risks.length > 1) vehicles = [this.getVehicleControl(), ...vehicles];

      this.vehicleId = null;
      this.vehicles.clear();
      if (vehicles?.length === 0) {
        this.addressForm.removeControl('vehiclesGaraged');
      } else {
        vehicles.forEach(v => this.vehicles.push(v));
        if (!this.addressForm.contains('vehiclesGaraged')) {
          this.addressForm.addControl(
            'vehiclesGaraged',
            new FormControl<Option>(this.garagedOptions[0], Validators.required)
          );
        }
        this.attachEventHandler();
      }
      this.addressForm.updateValueAndValidity();

      if (this.hasSelectAll)
        this.selectAll.valueChanges
          .pipe(
            takeUntil(this.stop$),
            tap(value => {
              this.updateAll(value);
            })
          )
          .subscribe();

      this.vehicles.valueChanges
        .pipe(
          filter(vs => vs?.length > 0),
          takeUntil(this.stop$),
          tap(vs => {
            if (this.hasSelectAll) {
              const vehicles = (vs as boolean[]).filter((v, i) => i !== 0);
              const allSelected = vehicles.every(v => v);
              if (allSelected) {
                if (!this.selectAll.value) this.selectAll.setValue(true, { emitEvent: false });
                this.selectAll.updateOn;
              } else {
                if (this.selectAll.value) this.selectAll.patchValue(false, { emitEvent: false });
              }
            }
          })
        )
        .subscribe();
    }
  }

  setVehicleOptions(vehicles: RiskModel[]) {
    const mapped = vehicles.map(
      v =>
        <Option>{
          id: v.vehicleId,
          text: v.description,
          icon: v.iconType,
          value: false
        }
    );
    const selectAllOption = this.addressChange.selectAllOption;
    this.vehicleOptions = mapped.length > 1 ? [selectAllOption, ...mapped] : [...mapped];
  }

  public onSubmit() {
    if (this.agents.length > 1) this.modalService.open(this.agentsModal);
    else this.sendToAgent(this.agents[0]);
  }

  sendToAgent(agent: AgentInfo) {
    this.closeModal();

    const data = this.addressForm.getRawValue();

    const vehicles = [];
    data.vehicles.forEach((v, i) => {
      if (v && i > 0) {
        vehicles.push(this.vehicleOptions[i]);
      }
    });

    let changeRequest = new ChangeRequest();
    changeRequest.policyChangeType = PolicyChangeRequestType.ADDRESS_CHANGE;

    const requestChange: RequestChange = {
      agentId: agent.agentId,
      agentName: agent.name,
      agencyEmail: agent.agencyEmails?.at(0),
      agentEmail: agent.emails?.at(0),
      customerFirstName: this.addressInfo.customerFirstName,
      customerName: this.addressInfo.customerName,
      emailAddress: this.addressInfo.emailAddress,
      mailingAddress: this.addressInfo.mailingAddress,
      customerId: this.addressInfo.customerId,
      phoneNumbers: this.addressInfo.phoneNumbers,
      policyNumbers: this.addressInfo.policyNumbers,
      partnerId: this.addressInfo.partnerId
    };

    const addressChange: AddressChange = {
      agentId: agent.agentId,
      agentName: agent.name,
      agencyEmail: agent.agencyEmails?.at(0),
      agentEmail: agent.emails?.at(0),
      customerId: this.addressInfo.customerId,
      partnerId: this.addressInfo.partnerId,
      customerFirstName: this.addressInfo.customerFirstName,
      customerName: this.addressInfo.customerName,
      emailAddress: this.addressInfo.emailAddress,
      existingAddress: this.addressInfo.mailingAddress,
      newAddress: `${data.address1}${data.address2 ? ' ' + data.address2 : ''} ${data.city}, ${data.state} ${data.zip}`,
      phoneNumbers: this.addressInfo.phoneNumbers,
      policyNumbers: this.addressInfo.policyNumbers,
      sendDocuments: data.sendDocuments?.value ? 'Yes' : 'No',
      vehiclesGaraged: this.vehiclesGaraged ? (data.vehiclesGaraged?.value ? 'Yes' : 'No') : 'N/A',
      vehicles: vehicles.map(v => v.text).join(', ') || 'N/A',
      reason: data.reason || 'N/A'
    };
    changeRequest.requestChange = requestChange;
    changeRequest.addressChange = addressChange;
    this.changeRequestAddress.emit(changeRequest);
  }

  onAgentSelected(agent: AgentInfo) {
    if (agent) this.sendToAgent(agent);
  }

  public cancel(): void {
    this.cancelEvent.emit();
  }

  closeModal() {
    this.modalService.close(this.agentsModal);
  }

  validateVehicles(formGroup: FormGroup): { [key: string]: any } | null {
    const vehiclesGaragedControl = formGroup.get('vehiclesGaraged');
    if (!vehiclesGaragedControl) return null;
    const vehiclesGaraged: Option = formGroup.get('vehiclesGaraged').value;
    const vehicles = formGroup.get('vehicles').value;
    if (vehiclesGaraged.value && !vehicles.some(v => v)) return { selectOneVehicle: true };
    return null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['risks'].firstChange && changes['risks'].currentValue) {
      if (this.newTemplate) {
        if (this._risks?.length > 0) {
          const vehiclesGaraged = this.vehiclesGaraged.value.value;
          this.addRemoveVehicles(vehiclesGaraged);
        } else {
          this.addressForm.removeControl('vehiclesGaraged');
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  // OLD TEMPLATE

  initializeOldTemplate() {
    // Get the states from the copy service
    this.states = this.copyService.getCopy('shared.usStates');

    // Build the address form
    this.addressGroup = this.buildAddressForm(this.formBuilder, this.currentAddress);

    // Initialize the form controls
    [
      this.streetAddressLine1Input,
      this.streetAddressLine2Input,
      this.cityInput,
      this.stateInput,
      this.zipcodeInput,
      this.commentInputCtrl
    ] = this.policyTransmuteService.initializeFormControls(this.addressGroup);

    this.addressGroup.valueChanges.pipe(take(1)).subscribe(() => {
      this.valueChangeEvent.emit();
    });
  }

  private buildAddressForm(fb: UntypedFormBuilder, currentAddress: PartyAddress): UntypedFormGroup {
    return fb.group({
      streetAddressLine1Input: [
        _get(currentAddress, 'line1', ''),
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(68),
          ValidationService.specialCharValidatorWithLetterAndNum,
          ValidationService.addressEntryValidator
        ])
      ],
      streetAddressLine2Input: [
        _get(currentAddress, 'line2', null),
        Validators.compose([
          ValidationService.specialCharValidatorWithLetterAndNum,
          ValidationService.addressEntryValidator,
          Validators.minLength(2),
          Validators.maxLength(68)
        ])
      ],
      cityInput: [
        _get(currentAddress, 'city', ''),
        Validators.compose([
          Validators.required,
          ValidationService.specialCharValidator,
          Validators.minLength(2),
          Validators.maxLength(30)
        ])
      ],
      stateInput: [_get(currentAddress, 'state', ''), Validators.compose([Validators.required])],
      zipcodeInput: [
        _get(currentAddress, 'zip5', ''),
        Validators.compose([Validators.required, ValidationService.zipCodeValidator])
      ],
      commentInputCtrl: [
        '',
        Validators.compose([Validators.maxLength(850), ValidationService.textInputValidator])
      ]
    });
  }

  public submit(): void {
    let changeRequest = new ChangeRequest();

    changeRequest = Object.assign({}, changeRequest, {
      policyChangeType: PolicyChangeRequestType.ADDRESS_CHANGE
    });
    changeRequest = Object.assign({}, changeRequest, { address: new Address() });
    changeRequest = Object.assign({}, changeRequest, {
      comment: this.commentInputCtrl.value
    });

    // Fetch the details from the address group form
    changeRequest = _merge(changeRequest, {
      address: {
        addressLine1: this.streetAddressLine1Input.value,
        addressLine2: this.streetAddressLine2Input.value,
        city: this.cityInput.value,
        state: this.stateInput.value,
        zipCode: this.zipcodeInput.value
      }
    });

    this.analyticsFacade.trackEvent({
      event: 'change_profile_success',
      profile_change_type: 'address'
    });

    // Emit the selected policies
    this.changeRequestAddress.emit(changeRequest);
  }

  // OLD TEMPLATE
}
